import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Download, Edit3, File, Folder, FolderOpen, Home, RefreshCw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fetchFileBlob } from "@/lib/api";

const fmtSize = (n) => n == null ? "-" : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

export default function Explorer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [path, setPath] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [matchedDoc, setMatchedDoc] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const crumbs = useMemo(() => {
    const parts = path ? path.split("/") : [];
    return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join("/") }));
  }, [path]);


  const parentPath = useMemo(() => {
    if (!path) return "";
    const parts = path.split("/");
    return parts.slice(0, -1).join("/");
  }, [path]);

  const load = async (nextPath = path, nextPage = 1, nextQuery = query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/explorer/tree", { params: { path: nextPath, page: nextPage, limit: 50, q: nextQuery } });
      setPath(data.path || "");
      setSearchParams(data.path ? { path: data.path } : {}, { replace: true });
      setItems(data.items || []);
      setPage(data.page || 1);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setSelected(null);
      setMatchedDoc(null);
      setMenu(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka folder."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load(searchParams.get("path") || "", 1, "");
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => load(path, 1, query), 350);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const openItem = (item) => {
    setMenu(null);
    if (item.type === "folder") {
      setQuery("");
      return load(item.path, 1, "");
    }
    setSelected(item);
    api.get("/explorer/match", { params: { path: item.path } })
      .then(({ data }) => setMatchedDoc(data.document))
      .catch(() => setMatchedDoc(null));
  };

  const downloadFile = async (item) => {
    if (item.type !== "file") return;
    try {
      const url = await fetchFileBlob(item.storage_path);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("File tidak bisa diunduh."); }
  };

  const openEditor = async (item) => {
    try {
      const { data } = await api.get("/onlyoffice/health");
      if (!data.ok) return toast.error(data.detail || "OnlyOffice belum jalan. Jalankan docker compose -f docker-compose.onlyoffice.yml up -d");
      navigate(`/explorer/edit?path=${encodeURIComponent(item.path)}&from=${encodeURIComponent(path)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OnlyOffice belum jalan. Jalankan docker compose -f docker-compose.onlyoffice.yml up -d");
    }
  };

  const renameItem = async (item) => {
    const name = window.prompt("Nama baru", item.name);
    if (!name || name === item.name) return;
    try {
      await api.put("/explorer/rename", { path: item.path, new_name: name });
      toast.success("Nama berhasil diubah.");
      load(path, page, query);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal rename."); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Hapus ${item.name}?`)) return;
    try {
      await api.delete("/explorer/item", { params: { path: item.path } });
      toast.success("Item dihapus.");
      load(path, page, query);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus."); }
  };

  const openMenu = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setSelected(item.type === "file" ? item : null);
    setMenu({ x: event.clientX, y: event.clientY, item });
  };

  const menuAction = (fn) => {
    const item = menu?.item;
    setMenu(null);
    if (item) fn(item);
  };

  return (
    <div className="h-screen overflow-hidden p-4 pt-16 sm:p-8 lg:p-12 max-w-7xl mx-auto animate-fade-up flex flex-col">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-gold/10 px-3 py-1 text-xs font-semibold text-gold mb-3"><FolderOpen className="w-4 h-4" /> Explorer Arsip</div>
        <h1 className="font-display text-4xl font-medium c-strong">Explorer Arsip</h1>
        <p className="text-sm c-muted mt-2">Klik kanan file/folder untuk rename atau edit. Tombol aksi cepat hanya hapus.</p>
      </div>

      <div className="c-card border rounded-2xl card-elev overflow-hidden flex min-h-0 flex-1 flex-col">
        <div className="p-4 border-b c-border flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 text-sm c-muted overflow-x-auto">
            <button onClick={() => { setQuery(""); load("", 1, ""); }} className="inline-flex items-center gap-1 hover:text-gold"><Home className="w-4 h-4" /> uploads</button>
            {crumbs.map((c) => <span key={c.path} className="inline-flex items-center gap-2"><ChevronRight className="w-4 h-4" /><button onClick={() => { setQuery(""); load(c.path, 1, ""); }} className="hover:text-gold whitespace-nowrap">{c.name}</button></span>)}
          </div>
          <div className="flex gap-2">
            <button onClick={() => load(parentPath, 1, query)} disabled={!path} className="inline-flex items-center gap-2 border c-border rounded-lg px-3 py-2 text-sm c-muted hover:text-gold disabled:opacity-40 disabled:cursor-not-allowed"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 c-muted" /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari nama file/folder..." className="border c-input rounded-lg pl-9 pr-3 py-2 text-sm" /></div>
            <button onClick={() => load(path, page, query)} className="border c-border rounded-lg px-3 py-2 c-muted hover:text-gold"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-h-0 overflow-hidden flex flex-col">
            <table className="w-full table-fixed text-sm text-left">
              <colgroup><col className="w-[42%]" /><col className="w-[13%]" /><col className="w-[15%]" /><col className="w-[20%]" /><col className="w-[10%]" /></colgroup>
              <thead className="c-soft c-muted text-xs uppercase tracking-wider"><tr><th className="px-5 py-3">Nama</th><th className="px-5 py-3">Tipe</th><th className="px-5 py-3">Ukuran</th><th className="px-5 py-3">Diubah</th><th className="px-5 py-3">Aksi</th></tr></thead>
            </table>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full table-fixed text-sm text-left">
                <colgroup><col className="w-[42%]" /><col className="w-[13%]" /><col className="w-[15%]" /><col className="w-[20%]" /><col className="w-[10%]" /></colgroup>
                <tbody>
                  {loading ? <tr><td colSpan={5} className="px-5 py-16 text-center c-muted">Memuat...</td></tr> : items.length === 0 ? <tr><td colSpan={5} className="px-5 py-16 text-center c-muted">{query ? "Tidak ada hasil pencarian." : "Folder kosong."}</td></tr> : items.map((item) => (
                    <tr key={item.path} onContextMenu={(e) => openMenu(e, item)} onDoubleClick={() => openItem(item)} className={`border-t c-border hover:bg-gold/5 cursor-pointer ${selected?.path === item.path ? "bg-gold/10" : ""}`}>
                      <td onClick={() => openItem(item)} className="px-5 py-3 font-medium c-strong"><div className="flex min-w-0 items-center gap-3">{item.type === "folder" ? <Folder className="w-5 h-5 shrink-0 text-gold" /> : <File className="w-5 h-5 shrink-0 text-cyan-500" />}<span className="truncate">{item.name}</span></div></td>
                      <td className="px-5 py-3 c-muted truncate">{item.type}</td>
                      <td className="px-5 py-3 c-muted font-mono truncate">{fmtSize(item.size)}</td>
                      <td className="px-5 py-3 c-muted font-mono text-xs truncate">{new Date(item.modified_at).toLocaleString("id-ID")}</td>
                      <td className="px-5 py-3"><button onClick={(e) => { e.stopPropagation(); deleteItem(item); }} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /> Hapus</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-2 border-t c-border px-4 py-3 text-xs c-muted sm:flex-row sm:items-center sm:justify-between">
              <span>Menampilkan {items.length} dari {total} item{query ? ` untuk "${query}"` : ""}</span>
              <div className="flex items-center gap-2">
                <button type="button" disabled={page <= 1 || loading} onClick={() => load(path, page - 1, query)} className="rounded-lg border c-border px-3 py-1.5 disabled:opacity-40">Sebelumnya</button>
                <span className="font-mono">{page}/{pages}</span>
                <button type="button" disabled={page >= pages || loading} onClick={() => load(path, page + 1, query)} className="rounded-lg border c-border px-3 py-1.5 disabled:opacity-40">Berikutnya</button>
              </div>
            </div>
          </div>

          <aside className="min-h-0 overflow-auto border-t lg:border-t-0 lg:border-l c-border p-5 c-soft">
            <h2 className="font-display text-xl c-strong mb-4">Detail</h2>
            {!selected ? <p className="text-sm c-muted">Pilih file untuk preview, download, atau edit metadata dokumen.</p> : (
              <div className="space-y-4">
                <div><div className="text-xs c-muted uppercase tracking-wider mb-1">File</div><div className="font-medium c-strong break-all">{selected.name}</div></div>
                <div><div className="text-xs c-muted uppercase tracking-wider mb-1">Path</div><div className="text-xs c-muted break-all">{selected.path}</div></div>
                <button onClick={() => downloadFile(selected)} className="w-full bg-gold text-white rounded-lg px-4 py-2 text-sm flex items-center justify-center gap-2"><Download className="w-4 h-4" /> Download</button>
                <button onClick={() => openEditor(selected)} className="w-full border border-cyan-500 text-cyan-600 rounded-lg px-4 py-2 text-sm flex items-center justify-center gap-2"><Edit3 className="w-4 h-4" /> Edit File</button>
                {matchedDoc ? <button onClick={() => navigate(`/arsip/${matchedDoc.slug || matchedDoc.id}`)} className="w-full border border-gold text-gold rounded-lg px-4 py-2 text-sm">Edit Metadata Dokumen</button> : <div className="text-xs c-muted">Belum terhubung ke metadata dokumen.</div>}
              </div>
            )}
          </aside>
        </div>
      </div>

      {menu && createPortal(
        <div className="fixed z-[9999] w-56 overflow-hidden rounded-xl border border-white/10 bg-[#101820]/95 shadow-2xl shadow-black/40 backdrop-blur-xl text-sm" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          <button onClick={() => menuAction(openItem)} className="w-full px-4 py-3 text-left text-white/80 hover:bg-gold/10 hover:text-gold">{menu.item.type === "folder" ? "Buka Folder" : "Pilih File"}</button>
          {menu.item.type === "file" && <button onClick={() => menuAction(openEditor)} className="w-full px-4 py-3 text-left text-white/80 hover:bg-cyan-400/10 hover:text-cyan-200">Edit File</button>}
          {menu.item.type === "file" && <button onClick={() => menuAction(downloadFile)} className="w-full px-4 py-3 text-left text-white/80 hover:bg-gold/10 hover:text-gold">Download</button>}
          <button onClick={() => menuAction(renameItem)} className="w-full px-4 py-3 text-left text-white/80 hover:bg-gold/10 hover:text-gold">Rename</button>
          <button onClick={() => menuAction(deleteItem)} className="w-full px-4 py-3 text-left text-red-300 hover:bg-red-500/10 hover:text-red-200">Hapus</button>
        </div>,
        document.body
      )}
    </div>
  );
}
